<script setup>
import { PlayIcon } from '@heroicons/vue/24/solid'
import { ref } from 'vue'

const props = defineProps({
    videoId: {
        type: String,
        required: true,
    },
    title: {
        type: String,
        default: 'YouTube video player',
    },
    thumbnailQuality: {
        type: String,
        default: 'maxresdefault',
        validator: (value) => ['default', 'mqdefault', 'hqdefault', 'sddefault', 'maxresdefault'].includes(value),
    },
    class: {
        type: String,
        default: '',
    },
})

const isLoaded = ref(false)
const thumbnailUrl = `https://img.youtube.com/vi/${props.videoId}/${props.thumbnailQuality}.jpg`

const loadVideo = () => {
    isLoaded.value = true
}
</script>

<template>
    <div
        :class="['relative overflow-hidden rounded-2xl bg-black', props.class]"
        @click="loadVideo"
    >
        <!-- Thumbnail with Play Button -->
        <div
            v-if="!isLoaded"
            class="group relative h-[20rem] cursor-pointer xl:h-[35rem]"
        >
            <!-- YouTube Thumbnail -->
            <img
                :src="thumbnailUrl"
                :alt="`Náhled videa: ${title}`"
                class="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105 xl:object-cover"
                loading="lazy"
            />

            <!-- Dark Overlay -->
            <div class="absolute inset-0 bg-black/20 transition-opacity duration-300 group-hover:bg-black/30" />

            <!-- Play Button -->
            <div class="absolute inset-0 flex items-center justify-center">
                <div
                    class="rounded-full bg-red-600 p-4 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:bg-red-700"
                >
                    <PlayIcon class="ml-1 size-8 text-white" />
                </div>
            </div>

            <!-- Privacy Notice -->
            <div class="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p class="text-sm text-white/90">
                    Kliknutím načtete video z YouTube. Tím souhlasíte s jejich zásadami ochrany osobních údajů.
                </p>
            </div>
        </div>

        <iframe
            v-if="isLoaded"
            :src="`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`"
            :title="title"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
            :class="props.class"
            class="h-full w-full"
        />
    </div>
</template>
